import { BbotWizard, PageLoadingSpinner } from "top-component-library";
import EditSettingsContainer from "top-component-library/EditSettingsContainer";

import AlertSourceStep from "./add-edit-staff-alert/AlertSourceStep";
import ConfirmStep from "./add-edit-staff-alert/ConfirmStep";
import CopyStep from "./add-edit-staff-alert/CopyStep";
import CustomAlertsStep from "./add-edit-staff-alert/CustomAlertsStep";
import RecipientStep from "./add-edit-staff-alert/RecipientStep";
import { useStaffAlertForm } from "./add-edit-staff-alert/useStaffAlertForm";

const USER_FLOW_STEPS = {
  customAlerts: ["contacts", "alertSource", "alerts", "confirm"],
  usePresetAlerts: ["contacts", "alertSource", "confirm"],
  copyExistingAlerts: ["contacts", "alertSource", "copy", "confirm"],
};

const USER_FLOW_PRETTY_NAMES = {
  customAlerts: "Custom alerts",
  usePresetAlerts: "Preset alerts",
  copyExistingAlerts: "Copied alerts",
};

const AddEditStaffAlert = ({
  selectedCustomer,
  onCancel,
  onSaveOrEditCallback,
  possibleStations,
  copyableRecipients = [],
  alertToEdit = null,
}) => {
  const form = useStaffAlertForm({ selectedCustomer, alertToEdit, copyableRecipients, onSaveOrEditCallback });

  const recipientStep = (
    <RecipientStep
      contactForm={form.contactForm}
      employeeArray={form.employeeArray}
      recipientToAdd={form.recipientToAdd}
      setRecipientToAdd={form.setRecipientToAdd}
      onSelectEmployee={form.selectEmployee}
    />
  );

  const customAlertsStep = (
    <CustomAlertsStep
      availableAlerts={form.availableAlerts}
      alertsToAdd={form.alertsToAdd}
      possibleStations={possibleStations}
      onAddAlert={form.addAlert}
      onChangeOption={form.changeAlertOption}
      onRemoveAlert={form.removeAlert}
    />
  );

  const stepContents = {
    contacts: { title: "Recipient", content: recipientStep, form: form.contactForm },
    alertSource: {
      title: "Alert setup",
      content: (
        <AlertSourceStep
          userFlow={form.userFlow}
          setUserFlow={form.setUserFlow}
          validContactMethods={form.validContactMethods}
          copyableRecipients={copyableRecipients}
        />
      ),
    },
    alerts: { title: "Alerts", content: customAlertsStep, disabled: form.alertsToAdd.length === 0 },
    copy: {
      title: "Copy from",
      content: (
        <CopyStep
          copyableRecipients={copyableRecipients}
          copyStaffId={form.copyStaffId}
          setCopyStaffId={form.setCopyStaffId}
        />
      ),
      disabled: !form.copyStaffId,
    },
    confirm: alertToEdit
      ? null
      : {
          title: "Confirm",
          content: (
            <ConfirmStep
              recipientToAdd={form.recipientToAdd}
              employees={form.employees}
              userFlow={form.userFlow}
              alertsToAdd={form.alertsToAdd}
              copyStaffId={form.copyStaffId}
              copyableRecipients={copyableRecipients}
            />
          ),
        },
  };

  return (
    <PageLoadingSpinner spinning={form.isLoading}>
      {alertToEdit ? (
        <EditSettingsContainer
          numChanges={1} // This can be deleted once the changes from restaurant details get merged in
          showSave={true}
          tabs={[
            { name: "Recipient", content: recipientStep },
            { name: "Alerts", content: customAlertsStep },
          ]}
          id="edit-staff-alert"
          onSave={() => form.save(true)}
          hideEditFunction={onCancel}
          objectToEditName="Staff Alert"
          selectedNames={[alertToEdit.name]}
        />
      ) : (
        <BbotWizard
          stepContents={stepContents}
          userFlowSteps={USER_FLOW_STEPS}
          userFlow={form.userFlow}
          setUserFlow={form.setUserFlow}
          userFlowsPrettyNames={USER_FLOW_PRETTY_NAMES}
          confirmationDescription="Are you sure you'd like to add this staff alert?"
          confirmationTitle="Add this alert?"
          resetFunction={onCancel}
          confirmationFunction={() => form.save(false)}
          stepsForbiddenToReturnTo={["contacts"]}
          isSelectedCustomerFlaggedForDovetail={false}
        />
      )}
    </PageLoadingSpinner>
  );
};

export default AddEditStaffAlert;
